// ProductImg — THE product-photo renderer for the Sales portal.
//
// Promoted here from analytics/modelExplorer/ (2026-08-10 Phase 1). Before that,
// the portal rendered photos from 19 places and each re-implemented a different
// subset of the same job: 8 had no `loading="lazy"`, 2 had no `onError` at all
// (a dead URL rendered as a silent blank), none used `decoding="async"`, and
// three separate helpers resolved the same URL. Every Sales image now comes
// through this component.
//
// Guarantees for every consumer:
//   src       image_small ONLY, rewritten onto OUR origin as `/img/...`.
//             image_large is the `_`-prefixed variant that 404s on the comax
//             server — never rendered on screen (Conventions #90 / #159).
//   loading   "lazy" by default. A caller may pass "eager" for a hero that is
//             above the fold the instant a surface opens.
//   decoding  "async" — always. Keeps image decode off the main thread.
//   onError   retried a bounded number of times, then falls back to the
//             placeholder (see RETRY below).
//   backdrop  pure #ffffff, placeholder included (Convention #51).
//
// A missing URL and a failed fetch render the SAME placeholder, in the same box,
// at the same size — so a broken photo never collapses a layout, and the two
// states are indistinguishable to the user by design (they are the same problem
// from their side: no picture). The no-URL case never touches the network.
//
// WHERE photos come from (2026-08-11 Phase 2). Every URL is rewritten onto our
// own origin as `/img/...`, which `vercel.json` proxies to the comax host at the
// edge — so the user's browser never contacts the third-party ERP server, which
// the 2026-08-10 audit identified as the surviving cause of per-user failures.
// The relative form is deliberate: it is same-origin on whichever alias the user
// is on. Anything that is NOT a comax URL (a Supabase Storage photo, say) is
// returned untouched, so this can only ever affect product photos.
//
// RETRY (2026-08-19 Phase 2.5). Moving the fetch to our edge did not make the
// comax origin reliable — it moved WHERE it fails. Measured live on production
// that morning: the first grid paint issued 30 concurrent /img requests and 18
// came back 502, while a scroll twelve minutes later issued 20 staggered
// requests and all 20 returned 200 — and two of the URLs that had 502'd served
// fine (200, image/jpeg) when re-fetched five minutes later. The failures are
// transient and burst-correlated: the origin drops a large share of CONCURRENT
// traffic and is healthy moments later. Until this, one 502 was permanent on
// screen — the component settled on its placeholder at the first error and never
// tried again, so the very first paint, which is the burst, was also the part
// that failed most, and only a reload could clear it.
//
// A failure now costs a delay instead of a photo: up to two further attempts,
// then exactly the old end state. Three properties are load-bearing, not taste:
//   - BOUNDED. Two retries, then the placeholder forever. A permanently dead URL
//     must still settle, and must not keep a timer or the network busy.
//   - JITTERED. Eighteen cells retrying on one clock rebuild the very burst that
//     produced the 502s. Each delay is spread +/-30%, so retries arrive smeared
//     across a window instead of as a second thundering herd.
//   - QUIET WHEN HEALTHY. Timers are created in the error path only. An image
//     that loads, and a product with no URL at all, cost zero extra requests and
//     zero timers.
// This narrows the symptom; it does not cure the origin. That is the Storage
// mirror (Phase 3), which is where the third-party dependency actually ends.
//
// THE MIRROR (2026-08-19 Phase 3 / P2). Phase 1 copied every product photo
// into our own `product-images` bucket, so there is now a copy we serve
// ourselves. A consumer that passes `barcode` gets that copy FIRST, from
// Supabase's CDN -- one lazy attempt, no retry and no cache-buster, because a
// miss there means the object is absent rather than the host being busy.
// Everything after it is the /img ladder above, unchanged. So the comax
// origin stops being where photos come from and becomes only what covers the
// gaps: a model added since the last sync, and the 18 barcodes that have no
// photo at the origin either. A consumer that passes no barcode is byte-for-
// byte what shipped before -- which is both the rollback lever and the reason
// this could ship before the upload-time sync (P3) exists.
//
// The delays mirror the policy SmartImg has run on the CATALOG image path since
// 2026-04 (`utils/imageUtils.js`: MAX_RETRIES 2, [1200, 3000], cache-buster on
// retries only) deliberately rather than by accident. The MECHANISM cannot be
// shared: SmartImg preloads through `new Image()` behind a 6-slot concurrency
// queue and an IntersectionObserver, while this renders a real
// `<img loading="lazy">` and lets the browser schedule it — and it has no Sales
// consumer (21 files, all Catalog/Developments). Sharing the numbers alone would
// have meant editing a LOC-grandfathered file (may shrink, never grow) to serve
// a portal it does not reach, so the policy is stated here, beside the evidence
// that sets it.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toSameOriginImageUrl } from '../../../utils/imageUrl';
import { productMirrorUrl } from './productMirrorUrl';
import styles from './ProductImg.module.css';

const MAX_RETRIES = 2;
const RETRY_1_MIN_MS = 900;
const RETRY_1_MAX_MS = 2500;
const RETRY_2_MS = 3500;
const JITTER = 0.3;

// The FIRST retry is spread uniformly across a 1.6s window rather than jittered
// around a point. The burst failures are produced at or near the edge and come
// back in 45-97ms, so seventeen cells fail within a few milliseconds of each
// other; a +/-30% band around 1.2s bunches their retries back into ~720ms and
// rebuilds the very burst that caused the 502s. A flat 900-2500ms decorrelates
// them. The second retry keeps the +/-30% band — by then they are already spread.
function retryDelayMs(spent) {
  if (spent === 0) return RETRY_1_MIN_MS + Math.round(Math.random() * (RETRY_1_MAX_MS - RETRY_1_MIN_MS));
  return Math.round(RETRY_2_MS * (1 + (Math.random() * 2 - 1) * JITTER));
}

// A retry MUST leave the browser, and getting that wrong once is why this
// comment is long. The first version of this component retried on the identical
// URL. It passed every local test — a stand-in server saw all three attempts —
// and was INERT in production: measured on the live bundle, a first-paint burst
// produced 17 final 502s and resource timing held exactly ONE entry per failed
// URL. Nothing re-attempted, nothing healed.
//
// The cause is our own cache header. `vercel.json` stamps
// `Cache-Control: public, max-age=86400` on every `/img/*` response, and a 502
// carrying explicit freshness is a STORABLE response, so the browser keeps the
// failure for a day and answers a same-URL re-attempt out of its own cache in
// 12-20ms without touching the network. That is also the mechanism behind the
// users for whom images "never" load: they are replaying yesterday's failures.
// The same URL with a query param appended LOADED (200) on the same page, in the
// same second — a unique URL is a different cache key, so it reaches the origin.
//
// So retries are cache-busted: attempt 1 requests the canonical URL untouched
// (its cache entry stays the one every other page load shares), and each retry
// appends `?r=<n>`. The residual is known and accepted: a browser that cached a
// 502 replays it on attempt 1 for up to 24h and then heals via the busted retry
// a second or two later. Conditioning that header on status is a separate
// decision — it also governs email and every cached GOOD image — and the real
// cure is Phase 3, the Storage mirror.

// Attempt 0 is the canonical URL, byte-for-byte what every other consumer asks
// for. Retries append `r`, chosen because nothing else in the app sets it on an
// image URL (SmartImg's Catalog path uses `_r`, a different file and param).
function attemptUrl(url, attempt) {
  if (attempt <= 0) return url;
  return url + (url.includes('?') ? '&' : '?') + 'r=' + attempt;
}

export function ProductImg({
  src,
  barcode,
  alt = '',
  className = '',
  fallbackGlyph = '◎',
  fallback,
  loading = 'lazy',
  title,
  style,
}) {
  const url = toSameOriginImageUrl(src, { relative: true });
  // OUR copy, tried first. Null whenever the caller passed no barcode --
  // which is the rollback lever: an unconverted consumer keeps today's chain
  // byte for byte. Null too when there is no `url`, so a barcode alone never
  // guesses at a mirrored object for a product that has no photo at all;
  // that product must still cost zero requests, exactly as before.
  const storageUrl = url ? productMirrorUrl(barcode) : null;
  // Attempt -1 is the mirror; 0 is the canonical /img URL and 1..MAX_RETRIES
  // are its cache-busted retries. With no mirror the chain starts at 0 and
  // every line below reduces to exactly what shipped in 59a7fb0c.
  const firstAttempt = storageUrl ? -1 : 0;
  // `live` renders the <img>; `waiting` holds the placeholder while a retry is
  // pending; `dead` is the old permanent-failure state, reached only once the
  // attempts are spent.
  const [phase, setPhase] = useState('live');
  // `attempt` is rendered (it forms the key AND the busted URL); `attemptRef` is
  // the authoritative counter the error handler reads, so a burst of error
  // events can never be reconciled against a stale render value. Only the retry
  // timer writes the state, and it writes exactly what the ref already holds.
  const [attempt, setAttempt] = useState(firstAttempt);
  // Has the CURRENT attempt painted? An un-loaded <img> still occupies the cell
  // and, with an empty alt, paints as a white rectangle — measured in production
  // at `complete:false, naturalWidth:0` for 30+ seconds.
  const [painted, setPainted] = useState(false);
  const attemptRef = useRef(firstAttempt);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  // A new URL deserves a fresh attempt — otherwise a recycled component keeps
  // showing the placeholder for a photo that would load fine. A pending timer
  // belongs to the OLD url and is dropped with it. The same cleanup runs on
  // unmount, which is what stops a scrolled-away cell from firing a retry — and
  // from setting state — after it is gone.
  useEffect(() => {
    attemptRef.current = firstAttempt;
    setAttempt(firstAttempt);
    setPainted(false);
    setPhase('live');
    return clearTimer;
  }, [url, firstAttempt, clearTimer]);

  const handleError = useCallback(() => {
    const spent = attemptRef.current;
    // The mirror gets exactly ONE shot, and no timer. A miss there is almost
    // never transient the way a comax 502 is -- it means the object is not on
    // the mirror at all: a model added since the last sync, or one of the 18
    // barcodes the origin itself has no photo for. Spending a retry there,
    // let alone a jittered one, only delays the fallback that is going to work.
    if (spent < 0) {
      attemptRef.current = 0;
      setAttempt(0);
      setPainted(false);
      return;
    }
    if (spent >= MAX_RETRIES) { setPhase('dead'); return; }
    attemptRef.current = spent + 1;
    // Show the placeholder immediately, then re-mount the <img> when the timer
    // fires. Unmounting is what makes the browser re-issue the request.
    setPhase('waiting');
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setAttempt(spent + 1);
      setPainted(false);
      setPhase('live');
    }, retryDelayMs(spent));
  }, [clearTimer]);

  const handleLoad = useCallback(() => setPainted(true), []);
  // `max-age=86400` lands on the 200 as well as the 502, so a re-mounted <img>
  // can decode from cache before React attaches onLoad — the event never fires
  // and the cell would hold its placeholder forever. Checking `complete` at
  // mount catches that.
  const measureRef = useCallback((node) => {
    if (node && node.complete && node.naturalWidth > 0) setPainted(true);
  }, []);
  const renderFallback = () => {
    // `fallback` lets a surface that already had a bespoke placeholder keep it
    // (the item-code glyph on a catalogue card, the big ◎ on a lightbox stage).
    // The guarantees are what this component owns; the look stays the caller's.
    // Without it, converting these sites would have silently restyled them.
    // A pending retry renders exactly this too — no spinner, no new visual
    // state, so a waiting cell is indistinguishable from a settled one and
    // nothing moves if the retry then succeeds.
    if (fallback !== undefined) return fallback;
    return (
      <div className={`${className} ${styles.piFallback}`} title={title} aria-label={alt || undefined} role="img">
        {fallbackGlyph}
      </div>
    );
  };

  const showImg = !!url && phase === 'live';
  // Only a RE-attempt holds the placeholder until its photo paints: hiding the
  // FIRST attempt of the chain -- the mirror when there is one, /img otherwise
  // -- would hide it from the lazy intersection check, and a lazy element that
  // is never visible never loads. Everything after it is on screen by
  // definition, because only a rendered image can have raised the error.
  const isRetry = attempt > firstAttempt;
  const holdFallback = !showImg || (isRetry && !painted);

  const img = (
    <img
      // The key is not decoration. Without it React reconciles the retry against
      // the <img> already in the DOM, and an element that keeps its node fires no
      // fresh load/error event — the machine then stalls in `live` behind an
      // already-errored image, which renders as an empty box (alt is ""), neither
      // photo nor placeholder. Changing the key destroys the node and builds a
      // new one, so every attempt is a real attempt.
      key={attempt}
      src={attempt < 0 ? storageUrl : attemptUrl(url, attempt)}
      alt={alt}
      title={title}
      className={`${className} ${styles.pi}`}
      // `display:none` keeps an un-painted retry out of the flow, so the cell
      // shows the placeholder and nothing shifts when the photo arrives; an
      // EAGER image still loads while hidden, which is why retries drop lazy.
      style={isRetry && !painted ? { ...style, display: 'none' } : style}
      // A retried element is on screen by definition, so deferring it is
      // meaningless — and in production the lazy pathway never scheduled the
      // re-mounted node's load (30+ s at naturalWidth 0) while `new Image()` on
      // the same URL fetched instantly. The first attempt keeps the caller's value.
      loading={isRetry ? 'eager' : loading}
      decoding="async"
      ref={measureRef}
      onLoad={handleLoad}
      onError={handleError}
    />
  );

  // Two fixed slots, always in this order: a stable shape stops React pairing
  // the placeholder with the <img> when one goes, which would rebuild the image
  // node and restart the load.
  return (
    <>
      {holdFallback ? renderFallback() : null}
      {showImg ? img : null}
    </>
  );
}
